import {
  DEFAULT_GUTTER_POINTS,
  GUTTER_FIT_EPSILON,
  MIN_GUTTER_POINTS,
} from '../constants/guide-limits'
import type { GuideUnit } from '../types/guide-unit'
import { formatFieldValue } from './gutter-formatting'
import { parseGutterToPoints } from './gutter-parsing'

function getMaxFittingGutterPoints(count: number, pageInnerSize: number): number {
  if (!Number.isFinite(pageInnerSize) || pageInnerSize <= 0) {
    return MIN_GUTTER_POINTS
  }

  if (!Number.isFinite(count) || count <= 1) {
    return Number.POSITIVE_INFINITY
  }

  return Math.max(MIN_GUTTER_POINTS, pageInnerSize / (count - 1) - GUTTER_FIT_EPSILON)
}

function getSafeDefaultGutterPoints(count: number, pageInnerSize: number): number {
  return Math.min(DEFAULT_GUTTER_POINTS, getMaxFittingGutterPoints(count, pageInnerSize))
}

export function normalizeStoredGutterPoints(
  value: number,
  count: number,
  pageInnerSize: number
): number {
  if (Number.isFinite(value) && value >= MIN_GUTTER_POINTS) {
    return value
  }

  return getSafeDefaultGutterPoints(count, pageInnerSize)
}

/** Re-formats a typed gutter value in `unit`, or null when it doesn't parse. */
export function normalizeGutterInput(
  rawValue: string,
  unit: GuideUnit,
  fieldCount: number,
  pageInnerSize: number
): string | null {
  const parsed = parseGutterToPoints(rawValue, unit)

  if (parsed.points == null) {
    return null
  }

  return formatFieldValue(
    normalizeStoredGutterPoints(parsed.points, fieldCount, pageInnerSize),
    unit,
    parsed.decimalPlaces
  )
}

type GutterAxis = { count: number; pageInnerSize: number }

/**
 * Blur normalization for one gutter field. `own` is the blurred field's new text; `other` is the
 * linked field's new text when the gutters are linked (normalized against its own axis, falling
 * back to `own`), else null. Returns null when the typed value doesn't parse (leave both as is).
 */
export function resolveGutterBlur(
  rawValue: string,
  unit: GuideUnit,
  linked: boolean,
  ownAxis: GutterAxis,
  otherAxis: GutterAxis
): { own: string; other: string | null } | null {
  const normalized = normalizeGutterInput(rawValue, unit, ownAxis.count, ownAxis.pageInnerSize)

  if (normalized == null) return null

  if (!linked) return { own: normalized, other: null }

  const linkedNormalized =
    normalizeGutterInput(rawValue, unit, otherAxis.count, otherAxis.pageInnerSize) ?? normalized

  return { own: normalized, other: linkedNormalized }
}
